import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { formatErr } from "@/lib/api";

/**
 * Custom Scope Items panel — Phase 1A-S.
 *
 * The heading text MUST be exactly "Custom Scope Items" per spec. This
 * panel is for client-requested work that does NOT fit a standard Luxe
 * scope heading (e.g. "Relocate air-conditioning controller",
 * "Artwork mounting", "Repair adjacent wall").
 *
 * Persistence: /api/projects/{pid}/custom-scope-items.
 */
export default function CustomScopeItemsPanel({ projectId, schematicItems = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState(blankDraft());

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/custom-scope-items`);
      setItems(data || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!draft.description) { toast.error("Description required"); return; }
    try {
      await api.post(`/projects/${projectId}/custom-scope-items`, draft);
      toast.success("Custom scope item added");
      setShowNew(false); setDraft(blankDraft()); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this Custom Scope Item?")) return;
    try { await api.delete(`/custom-scope-items/${id}`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="luxe-card p-6" data-testid="custom-scope-items-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline mb-1">Custom Scope Items</div>
          <div className="text-xs text-muted-foreground">
            Client-requested work outside the standard Subtend Studio scope headings.
          </div>
        </div>
        <button data-testid="btn-add-custom-scope-item"
                onClick={() => setShowNew(true)}
                className="btn-outline text-xs">
          <Plus className="w-3 h-3" strokeWidth={1.5}/> Add
        </button>
      </div>

      {showNew && (
        <div className="border border-border p-4 mb-4 space-y-3 bg-muted/40" data-testid="custom-scope-new-form">
          <TextField label="Description" testid="csi-description"
                     value={draft.description}
                     onChange={(v) => setDraft({ ...draft, description: v })}/>
          <TextField label="Client-facing description (optional)" testid="csi-client-description"
                     value={draft.client_description}
                     onChange={(v) => setDraft({ ...draft, client_description: v })}/>
          <div className="grid grid-cols-3 gap-2">
            <TextField label="Trade / Category" testid="csi-trade"
                       value={draft.trade}
                       onChange={(v) => setDraft({ ...draft, trade: v })}/>
            <NumField label="Quantity" testid="csi-quantity"
                       value={draft.quantity}
                       onChange={(v) => setDraft({ ...draft, quantity: v })}/>
            <TextField label="Unit" testid="csi-unit"
                       value={draft.unit}
                       onChange={(v) => setDraft({ ...draft, unit: v })}/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="overline text-[10px] block mb-1">Linked schematic item</label>
              <select data-testid="csi-linked-schematic"
                      className="luxe-input"
                      value={draft.linked_schematic_id || ""}
                      onChange={(e) => setDraft({ ...draft, linked_schematic_id: e.target.value || null })}>
                <option value="">— None —</option>
                {schematicItems.map((it) => (
                  <option key={it.id} value={it.id}>{it.label || it.type}</option>
                ))}
              </select>
            </div>
            <NumField label="Allowance ($)" testid="csi-allowance"
                       value={(draft.sell_cents || 0) / 100}
                       onChange={(v) => setDraft({ ...draft, sell_cents: Math.round((v || 0) * 100) })}/>
          </div>
          <TextField label="Notes (internal)" testid="csi-internal-notes"
                     value={draft.internal_notes}
                     onChange={(v) => setDraft({ ...draft, internal_notes: v })}/>
          <div className="grid grid-cols-2 gap-1 text-[11px]">
            <Toggle testid="csi-include-proposal"          label="On Proposal"                v={draft.include_on_proposal}          on={(x) => setDraft({ ...draft, include_on_proposal: x })}/>
            <Toggle testid="csi-include-construction-docs" label="On Construction Docs"        v={draft.include_on_construction_docs} on={(x) => setDraft({ ...draft, include_on_construction_docs: x })}/>
            <Toggle testid="csi-include-finishes"          label="On Finishes Schedule"       v={draft.include_on_finishes_schedule} on={(x) => setDraft({ ...draft, include_on_finishes_schedule: x })}/>
            <Toggle testid="csi-include-product-schedule"  label="On Product Schedule"        v={draft.include_on_product_schedule}  on={(x) => setDraft({ ...draft, include_on_product_schedule: x })}/>
            <Toggle testid="csi-include-procurement"       label="On Procurement List"        v={draft.include_on_procurement_list}  on={(x) => setDraft({ ...draft, include_on_procurement_list: x })}/>
          </div>
          <div className="flex gap-2 justify-end">
            <button data-testid="btn-cancel-csi" onClick={() => { setShowNew(false); setDraft(blankDraft()); }} className="btn-outline text-xs">Cancel</button>
            <button data-testid="btn-save-csi" onClick={create} className="btn-accent text-xs">Save</button>
          </div>
        </div>
      )}

      {loading && <div className="text-xs text-muted-foreground">Loading…</div>}
      {!loading && !items.length && (
        <div className="text-xs text-muted-foreground border border-dashed border-border p-4 text-center" data-testid="csi-empty">
          No custom scope items yet.
        </div>
      )}
      {!loading && items.length > 0 && (
        <ul className="divide-y divide-border" data-testid="csi-list">
          {items.map((c) => (
            <li key={c.id} className="py-3 flex items-start gap-3" data-testid={`csi-row-${c.id}`}>
              <div className="flex-1">
                <div className="text-sm font-medium">{c.description}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {c.trade ? `${c.trade} · ` : ""}{c.quantity ?? 1} {c.unit || "each"}
                  {c.sell_cents ? ` · $${(c.sell_cents / 100).toFixed(2)}` : ""}
                </div>
              </div>
              <button data-testid={`btn-delete-csi-${c.id}`}
                      onClick={() => remove(c.id)}
                      className="text-muted-foreground hover:text-red-600">
                <Trash2 className="w-3 h-3" strokeWidth={1.5}/>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function blankDraft() {
  return {
    description: "", client_description: "", trade: "", quantity: 1, unit: "each",
    linked_schematic_id: null, notes: "", internal_notes: "",
    allowance_cents: null, sell_cents: null,
    include_on_proposal: true, include_on_construction_docs: true,
    include_on_finishes_schedule: false, include_on_product_schedule: false,
    include_on_procurement_list: true,
  };
}
function TextField({ label, testid, value, onChange }) {
  return (
    <div>
      <label className="overline text-[10px] block mb-1">{label}</label>
      <input data-testid={testid} className="luxe-input" value={value || ""} onChange={(e) => onChange(e.target.value)}/>
    </div>
  );
}
function NumField({ label, testid, value, onChange }) {
  return (
    <div>
      <label className="overline text-[10px] block mb-1">{label}</label>
      <input data-testid={testid} type="number" className="luxe-input" value={value ?? 0} onChange={(e) => onChange(parseFloat(e.target.value) || 0)}/>
    </div>
  );
}
function Toggle({ label, testid, v, on }) {
  return (
    <label className="flex items-center gap-1 cursor-pointer">
      <input type="checkbox" data-testid={testid} checked={!!v} onChange={(e) => on(e.target.checked)}/>
      <span>{label}</span>
    </label>
  );
}
